import { CDN_BASE } from './cdn';
/**
 * Timeline loader — fetches /data/timeline.json (generated by corpllm-server
 * engine/cmd/fixture) and exposes typed records for the Playback screen.
 *
 * Types mirror corpllm-server/engine/internal/timeline/types.go.
 */

// ── Types ─────────────────────────────────────────────────────────────────

export interface TimelineMeta {
  day: number;
  seed: number;
  ticks_per_day: number;
  player: string;
  engine_version?: string;
}

export interface MovementRecord {
  id: string;
  entity_id: string;
  entity_type: 'runner' | 'vehicle';
  from_node: string;
  to_node: string;
  tick_start: number;
  tick_end: number;
  path?: string[];
}

export interface EventRecord {
  id: string;
  tick: number;
  node_id: string;
  event_type: string;
  result: string;
  actor: string;
  delta_cash: number;
  delta_heat: number;
  detail?: string;
}

export interface CombatRecord {
  id: string;
  tick_start: number;
  tick_end: number;
  node_id: string;
  attacker_id: string;
  defender_id: string;
  outcome: 'attacker_win' | 'defender_win' | 'draw';
}

export interface TimelineOutput {
  meta: TimelineMeta;
  movements: MovementRecord[];
  events: EventRecord[];
  combats: CombatRecord[];
}

// ── Event categories (spec §9.5 — 7 Kategorien) ──────────────────────────
//
// Playback filter buttons map each event_type to one of these categories
// so players can toggle entire families (all combat, all hacking, …) with
// a single click. Mapping mirrors the server-side timelineEventType
// whitelist in corpllm-server/engine/internal/tick/phase3_action.go.

export type EventCategory =
  | 'kampf'
  | 'transport'
  | 'bestechung'
  | 'begegnungen'
  | 'hacking'
  | 'verhaftung'
  | 'staatlich';

export const EVENT_CATEGORIES: readonly EventCategory[] = [
  'kampf', 'transport', 'bestechung', 'begegnungen', 'hacking', 'verhaftung', 'staatlich',
];

const CATEGORY_MAP: Record<string, EventCategory> = {
  // Kampf — ATTACK/ASSAULT + RAID + targeted violence. Spec §9.5 lists
  // RAID, ASSASSINATE, AMBUSH, MUG + "Beistand-Kämpfe" (→ ASSAULT).
  ASSAULT: 'kampf',
  RAID: 'kampf',
  ASSASSINATE: 'kampf', // legacy event-type (pre-Target-Polymorphie replays)
  ELIMINATE: 'kampf',   // #211 — verb renamed ASSASSINATE→ELIMINATE; keep both
  AMBUSH: 'kampf',
  MUG: 'kampf',
  DRIVE_BY: 'kampf',
  ARSON: 'kampf',
  BOMB: 'kampf',
  DRONE_STRIKE: 'kampf',
  SNIPE: 'kampf',
  // Bestechung
  BRIBE: 'bestechung',
  LOBBY: 'bestechung',
  PLANT_STORY: 'bestechung',
  POACH: 'bestechung',
  BLACKMAIL: 'bestechung',
  SLANDER: 'bestechung',
  // Begegnungen — street-hustle, infiltration, spy plumbing
  EXTORT: 'begegnungen',
  COLLECT: 'begegnungen',
  SCOUT: 'begegnungen',
  PLACE_SPY: 'begegnungen',
  UNDERCOVER: 'begegnungen',
  SHADOW: 'begegnungen',
  BUG: 'begegnungen',
  INFILTRATE: 'begegnungen',
  BURGLE: 'begegnungen',
  PICKPOCKET: 'begegnungen',
  INTERROGATE: 'begegnungen',
  INCITE_RIOT: 'begegnungen',
  // Hacking
  HACK_RECON: 'hacking',
  HACK_FREEZE: 'hacking',
  HACK_REROUTE: 'hacking',
  HACK_AUDIT: 'hacking',
  HACK_SIPHON: 'hacking',
  TRACE_HACK: 'hacking',
  EMP: 'hacking',
  DATA_WIPE: 'hacking',
  DOXX: 'hacking',
  CRYPTO_STEAL: 'hacking',
  MALWARE: 'hacking',
  CYBERCRIME: 'hacking',
  SPY: 'hacking',
  // Transport / Recruitment / Supply
  TRANSPORT_GOODS: 'transport',
  SMUGGLE: 'transport',
  SUPPLY_ILLEGAL: 'transport',
  RECRUIT: 'transport',
  // Verhaftung — server emits ARRESTED/ARREST/RELEASE/INTERROGATE-outcomes;
  // synonyms kept so upstream naming can drift without a client patch.
  ARREST: 'verhaftung',
  ARRESTED: 'verhaftung',
  RELEASE: 'verhaftung',
  KIDNAP: 'verhaftung',
  TORTURE: 'verhaftung',
  // Staatlich — government-initiated events
  CORPSEC_RAID: 'staatlich',
  FOB_AUDIT: 'staatlich',
  FCA_ACTIVATION: 'staatlich',
  EDICT: 'staatlich',
  VANDALIZE: 'staatlich',
  SABOTAGE: 'staatlich',
};

/** Resolves an event_type string to its spec §9.5 category.
 *  Returns undefined for unknown types. */
export function categoryOf(eventType: string): EventCategory | undefined {
  return CATEGORY_MAP[eventType];
}

// ── Loader ────────────────────────────────────────────────────────────────

export let timeline: TimelineOutput | null = null;

/**
 * Loads /data/timeline.json. Returns null if the file is missing or invalid.
 */
export async function loadTimeline(): Promise<TimelineOutput | null> {
  try {
    const res = await fetch(`${CDN_BASE}/data/timeline.json`);
    if (!res.ok) return null;
    const data: TimelineOutput = await res.json();
    timeline = data;
    console.info(`[timeline] loaded — ${data.movements.length} movements, ${data.events.length} events, ${data.combats.length} combats`);
    return data;
  } catch {
    return null;
  }
}
